import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { vendorFetch } from '../../contexts/VendorAuthContext';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function VendorVerify() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('loading'); // loading | success | error | already_verified
  const [errMsg, setErrMsg] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) { setState('error'); setErrMsg('No verification token in the link. Please check your email for the correct link.'); return; }
    vendorFetch('/partner/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) })
      .then((d) => {
        if (d?.message?.includes('already')) setState('already_verified');
        else setState('success');
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || err?.message || '';
        setState('error');
        setErrMsg(msg || 'This verification link is invalid or has expired.');
      });
  }, [token]);

  const resend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) { toast.error('Enter your registered email'); return; }
    setResending(true);
    try {
      await vendorFetch('/partner/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email: resendEmail.trim().toLowerCase() }),
      });
      setResent(true);
      toast.success('New verification link sent — check your inbox and spam folder');
    } catch {
      toast.error('Could not send. Please try again or contact Ellora EPC procurement.');
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4" data-testid="vendor-verify-page">
      <div className="bg-white rounded-sm shadow-2xl max-w-md w-full p-8 space-y-4">
        {/* Logo */}
        <div className="text-center mb-2">
          <img src="/ellora-logo-blue.png" alt="Ellora EPC" className="h-8 w-auto mx-auto mb-3" />
        </div>

        {/* Loading */}
        {state === 'loading' && (
          <div className="text-center space-y-3 py-4">
            <Loader2 size={36} className="animate-spin text-[#3b4872] mx-auto" />
            <div className="font-bold text-lg text-slate-900">Verifying your email…</div>
            <p className="text-sm text-slate-500">This should only take a moment.</p>
          </div>
        )}

        {/* Success */}
        {state === 'success' && (
          <div className="text-center space-y-3 py-4" data-testid="verify-success">
            <CheckCircle2 size={40} className="text-emerald-500 mx-auto" />
            <div className="font-bold text-xl text-slate-900">Email Verified!</div>
            <p className="text-slate-600 text-sm">Your account is now active. You can sign in to the partner portal.</p>
            <Link to="/partner/login"
              className="inline-block bg-[#3b4872] text-white px-8 py-2.5 rounded-sm font-bold text-sm hover:bg-[#2d3856]">
              Sign In →
            </Link>
          </div>
        )}

        {/* Already verified */}
        {state === 'already_verified' && (
          <div className="text-center space-y-3 py-4">
            <CheckCircle2 size={40} className="text-blue-500 mx-auto" />
            <div className="font-bold text-lg text-slate-900">Already Verified</div>
            <p className="text-slate-600 text-sm">Your email is already verified. You can log in.</p>
            <Link to="/partner/login" className="inline-block bg-[#3b4872] text-white px-6 py-2.5 rounded-sm font-bold text-sm hover:bg-[#2d3856]">
              Sign In →
            </Link>
          </div>
        )}

        {/* Error — show resend form, NOT "register again" */}
        {state === 'error' && (
          <div className="space-y-4" data-testid="verify-error">
            <div className="text-center">
              <XCircle size={36} className="text-red-500 mx-auto mb-3" />
              <div className="font-bold text-lg text-slate-900">Link Invalid or Expired</div>
              <p className="text-sm text-slate-500 mt-1">
                {errMsg || 'This verification link is no longer valid.'}
              </p>
            </div>
            {!resent ? (
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 space-y-3">
                <div className="flex items-center gap-2 text-xs font-bold text-blue-800">
                  <Mail size={14} /> Get a new verification link
                </div>
                <p className="text-xs text-blue-700">
                  Enter your registered email and we'll send a fresh link.
                </p>
                <form onSubmit={resend} className="flex gap-2">
                  <input type="email" value={resendEmail} onChange={e => setResendEmail(e.target.value)}
                    placeholder="your@company.com"
                    className="flex-1 border border-blue-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    data-testid="resend-verify-email" required />
                  <button type="submit" disabled={resending}
                    className="px-4 py-2 bg-[#3b4872] text-white rounded-sm text-xs font-bold disabled:opacity-50"
                    data-testid="resend-verify-btn">
                    {resending ? <Loader2 size={13} className="animate-spin inline" /> : 'Resend'}
                  </button>
                </form>
                <p className="text-[10px] text-blue-600">Check both your inbox and spam folder after clicking Resend.</p>
              </div>
            ) : (
              <div className="text-center bg-emerald-50 border border-emerald-200 rounded-sm p-4">
                <CheckCircle2 size={20} className="text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-emerald-800 font-semibold">New link sent!</p>
                <p className="text-xs text-emerald-700 mt-1">Check your inbox and spam folder. The link is valid for 24 hours.</p>
              </div>
            )}
            <div className="text-center">
              <Link to="/partner/login" className="text-xs text-slate-400 hover:text-slate-600">
                ← Back to Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
